import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, InputBase, Select, MenuItem, FormControl, CircularProgress, Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import useTheme from '../hooks/useTheme';
import ReportModelCustomer from './ReportModelCustomer';
import { fetchWithCache, fetchAllPages, performanceMonitor, debounce } from '../utils/apiCache';

export default function Customer() {
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReports, setSelectedReports] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  // Debounced search effect
  const debouncedSearchUpdate = useMemo(
    () => debounce((searchTerm) => {
      setDebouncedSearch(searchTerm);
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSearchUpdate(search);
  }, [search, debouncedSearchUpdate]);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log('🚀 Starting paginated Users fetch...');
        const data = await performanceMonitor.measureApiCall(
          'Customer/users-paginated',
          fetchAllPages('https://api.uxhq.com/users')
        );

        console.log(`📊 Users: Loaded ${data.length} total users`);
        setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users data. Please try again.');
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Memoized filtering for better performance
  const filteredUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];
    
    return users.filter(user =>
      (user.name || '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      (user.user_email || '').toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [users, debouncedSearch]);

  // Handle report selection
  const handleReportSelect = (userId, reportDate) => {
    // Only store the report date if it's a valid selection
    if (reportDate) {
      setSelectedReports(prev => ({
        ...prev,
        [userId]: reportDate
      }));
    } else {
      // Clear the selection if empty
      setSelectedReports(prev => {
        const newState = { ...prev };
        delete newState[userId];
        return newState;
      });
    }
  };

  // Handle report selection and open modal
  const handleReportNavigation = (userId, reportDate, userEmail) => {
    if (reportDate) {
      const date = new Date(reportDate);
      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
      
      console.log('Opening modal with:', { userEmail, reportDate: dateStr });
      
      setSelectedReport({
        userEmail: userEmail,
        reportDate: dateStr
      });
      setModalOpen(true);
    }
  };

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedReport(null);
  }, []);

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await performanceMonitor.measureApiCall(
        'Customer/users-refresh',
        fetchWithCache('https://api.uxhq.com/users', {}, 0)
      );

      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error refreshing users:', err);
      setError('Failed to refresh users data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <Box sx={theme.styles.loadingContainer}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ color: theme.textSecondaryColor }}>Loading users...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={theme.styles.pageContainer}>
        <Typography variant="h4" sx={{ ...theme.styles.pageTitle, mb: 3 }}>
          USERS
        </Typography>
        <Card sx={{ ...theme.styles.card, p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={handleRefresh} sx={theme.styles.button}>
            Try Again
          </Button>
        </Card>
      </Box>
    );
  }

  // Format date and time for display
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: '2-digit' 
    }).replace(/\//g, '-');
    const timeStr = date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    return `${dateStr} ${timeStr}`;
  };

  return (
    <Box sx={theme.styles.pageContainer}>
      {/* Page Title */}
      <Typography variant="h4" sx={{ ...theme.styles.pageTitle, mb: 3 }}>
        USERS
      </Typography>

      {/* Search Bar */}
      {theme.state?.display?.showSearchBar !== false && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Box sx={theme.styles.searchBar}>
            <SearchIcon sx={{ color: theme.textSecondaryColor, mr: 1 }} />
            <InputBase
              placeholder="search by name or email"
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={theme.styles.searchInput}
            />
          </Box>
        </Box>
      )}

      {/* Users Table */}
      <Card sx={theme.styles.card}>
        <TableContainer component={Paper} sx={{ ...theme.styles.card, borderRadius: 0 }}>
          <Table>
            <TableHead>
              <TableRow sx={theme.styles.tableHeader}>
                <TableCell sx={theme.styles.tableHeaderCell}>EMAIL</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>NAME</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>COUNTRY</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>ROLE</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}># REPORTS</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>REPORTS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user, idx) => (
                <TableRow key={user.user_id} hover>
                  <TableCell sx={theme.styles.tableCell}>{user.user_email}</TableCell>
                  <TableCell sx={theme.styles.tableCell}>
                    {user.name && user.name.length > 20 ? `${user.name.substring(0, 20)}...` : user.name || '-'}
                  </TableCell>
                  <TableCell sx={theme.styles.tableCell}>{user.country || '-'}</TableCell>
                  <TableCell sx={theme.styles.tableCell}>
                    {user.role && user.role.length > 25 ? `${user.role.substring(0, 25)}...` : user.role || '-'}
                  </TableCell>
                  <TableCell sx={theme.styles.tableCell}>
                    {user.reports_count ? user.reports_count.toString().padStart(3, '0') : '000'}
                  </TableCell>
                  <TableCell>
                    {user.reports_count > 0 && user.report_dates && user.report_dates.length > 0 ? (
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={selectedReports[user.user_id] || ''}
                          onChange={(e) => {
                            handleReportSelect(user.user_id, e.target.value);
                            if (e.target.value) {
                              handleReportNavigation(user.user_id, e.target.value, user.user_email);
                            }
                          }}
                          displayEmpty
                          sx={{
                            '& .MuiSelect-select': {
                              color: theme.textColor,
                              fontSize: '14px'
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.borderColor
                            }
                          }}
                        >
                          <MenuItem value="">
                            <Typography sx={{ color: theme.textSecondaryColor, fontSize: '14px' }}>
                              Select Report
                            </Typography>
                          </MenuItem>
                          {user.report_dates.map((reportDate, index) => (
                            <MenuItem 
                              key={index} 
                              value={reportDate.created_at}
                            >
                              {formatDateTime(reportDate.created_at)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography sx={{ color: theme.textSecondaryColor }}>-</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      
      {/* Report Modal */}
      <ReportModelCustomer
        open={modalOpen}
        onClose={handleCloseModal}
        userEmail={selectedReport?.userEmail}
        reportDate={selectedReport?.reportDate}
      />
    </Box>
  );
} 